import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Link, Plus, Copy, Trash2, Loader2, Clock, Users, CheckCircle,
  Settings, Shield, MessageSquare, UserPlus, Save, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import type { Json } from '@/integrations/supabase/types';

interface InviteLink {
  id: string;
  code: string;
  created_by: string;
  group_ids: string[];
  auto_approve: boolean;
  max_uses: number;
  uses: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  label: string | null;
}

interface GroupItem {
  id: string;
  name: string;
}

interface OnboardingSettings {
  requireInviteLink: boolean;
  defaultGroupIds: string[];
  welcomeMessage: string;
  postSignupRedirect: 'editor' | 'kiosk';
  defaultAutoApprove: boolean;
  defaultMaxUses: number;
  defaultExpiryDays: number;
}

const DEFAULT_ONBOARDING: OnboardingSettings = {
  requireInviteLink: false,
  defaultGroupIds: [],
  welcomeMessage: '',
  postSignupRedirect: 'editor',
  defaultAutoApprove: true,
  defaultMaxUses: 1,
  defaultExpiryDays: 7,
};

export const InviteLinks: React.FC = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState<InviteLink[]>([]);
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Onboarding settings
  const [onboarding, setOnboarding] = useState<OnboardingSettings>(DEFAULT_ONBOARDING);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [onboardingDirty, setOnboardingDirty] = useState(false);

  // Form state
  const [linkLabel, setLinkLabel] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [autoApprove, setAutoApprove] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [expiryDays, setExpiryDays] = useState(7);

  const fetchLinks = useCallback(async () => {
    const { data } = await supabase
      .from('invite_links')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setLinks(data.map(l => ({
        ...l,
        group_ids: (l.group_ids as string[]) || [],
      })));
    }
    setLoading(false);
  }, []);

  const fetchGroups = useCallback(async () => {
    const { data } = await supabase.from('groups').select('id, name').order('name');
    if (data) setGroups(data);
  }, []);

  const fetchOnboardingSettings = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'onboarding')
        .maybeSingle();

      if (data?.value) {
        setOnboarding({ ...DEFAULT_ONBOARDING, ...(data.value as unknown as Partial<OnboardingSettings>) });
      }
    } catch (e) {
      console.error('Failed to load onboarding settings:', e);
    } finally {
      setOnboardingLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
    fetchGroups();
    fetchOnboardingSettings();
  }, [fetchLinks, fetchGroups, fetchOnboardingSettings]);

  // When onboarding settings load, apply defaults to create form
  useEffect(() => {
    if (onboardingLoaded) {
      setAutoApprove(onboarding.defaultAutoApprove);
      setMaxUses(onboarding.defaultMaxUses);
      setExpiryDays(onboarding.defaultExpiryDays);
      setSelectedGroups(onboarding.defaultGroupIds);
    }
  }, [onboardingLoaded]);

  const updateOnboarding = (patch: Partial<OnboardingSettings>) => {
    setOnboarding(prev => ({ ...prev, ...patch }));
    setOnboardingDirty(true);
  };

  const saveOnboardingSettings = async () => {
    setSavingOnboarding(true);
    try {
      const { data: existing } = await supabase
        .from('app_settings')
        .select('id')
        .eq('key', 'onboarding')
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('app_settings')
          .update({ value: onboarding as unknown as Json })
          .eq('key', 'onboarding');
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('app_settings')
          .insert({ key: 'onboarding', value: onboarding as unknown as Json, description: 'Onboarding configuration' });
        if (error) throw error;
      }

      toast.success('Onboarding settings saved');
      setOnboardingDirty(false);
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingOnboarding(false);
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreate = async () => {
    if (!user) return;
    setCreating(true);

    try {
      const code = generateCode();
      const expiresAt = expiryDays > 0
        ? new Date(Date.now() + expiryDays * 86400000).toISOString()
        : null;

      const { error } = await supabase
        .from('invite_links')
        .insert({
          code,
          created_by: user.id,
          group_ids: selectedGroups,
          auto_approve: autoApprove,
          max_uses: maxUses,
          expires_at: expiresAt,
          label: linkLabel.trim() || null,
        });

      if (error) throw error;

      const url = `${window.location.origin}/auth?invite=${code}`;
      await navigator.clipboard.writeText(url);
      toast.success('Invite link created and copied to clipboard!');
      setCreateOpen(false);
      resetForm();
      fetchLinks();
    } catch (e) {
      toast.error('Failed to create invite link');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setLinkLabel('');
    setMaxUses(onboarding.defaultMaxUses);
    setAutoApprove(onboarding.defaultAutoApprove);
    setSelectedGroups([...onboarding.defaultGroupIds]);
    setExpiryDays(onboarding.defaultExpiryDays);
  };

  const copyLink = (code: string) => {
    const url = `${window.location.origin}/auth?invite=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  const toggleActive = async (id: string, active: boolean) => {
    const { error } = await supabase
      .from('invite_links')
      .update({ is_active: active })
      .eq('id', id);
    if (error) {
      toast.error('Failed to update link');
    } else {
      toast.success(active ? 'Link reactivated' : 'Link deactivated');
      fetchLinks();
    }
  };

  const deleteLink = async (id: string) => {
    if (!confirm('Delete this invite link?')) return;
    const { error } = await supabase.from('invite_links').delete().eq('id', id);
    if (error) {
      toast.error('Failed to delete link');
    } else {
      toast.success('Link deleted');
      fetchLinks();
    }
  };

  const isExpired = (link: InviteLink) => {
    return link.expires_at && new Date(link.expires_at) < new Date();
  };

  const isUsedUp = (link: InviteLink) => {
    return link.max_uses > 0 && link.uses >= link.max_uses;
  };

  const getGroupNames = (groupIds: string[]) => {
    return groupIds
      .map(id => groups.find(g => g.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const activeLinks = links.filter(l => l.is_active && !isExpired(l) && !isUsedUp(l));
  const inactiveLinks = links.filter(l => !l.is_active || isExpired(l) || isUsedUp(l));

  return (
    <>
      {/* Registration Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Registration Policy
          </CardTitle>
          <CardDescription>
            Control how new users can register and what happens after they sign up
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Require Invite Link</Label>
              <p className="text-xs text-muted-foreground">
                When enabled, users can only register via a valid invite link
              </p>
            </div>
            <Switch
              checked={onboarding.requireInviteLink}
              onCheckedChange={(v) => updateOnboarding({ requireInviteLink: v })}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Auto-Approve by Default</Label>
              <p className="text-xs text-muted-foreground">
                New invite links will default to auto-approving users
              </p>
            </div>
            <Switch
              checked={onboarding.defaultAutoApprove}
              onCheckedChange={(v) => updateOnboarding({ defaultAutoApprove: v })}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Post-Signup Redirect</Label>
            <Select
              value={onboarding.postSignupRedirect}
              onValueChange={(v) => updateOnboarding({ postSignupRedirect: v as 'editor' | 'kiosk' })}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">Label Editor</SelectItem>
                <SelectItem value="kiosk">Kiosk Mode</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Where new users are redirected after completing registration
            </p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Default Groups for New Users</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Groups automatically assigned to users who register via invite links
            </p>
            {groups.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded-lg bg-muted/30">
                {groups.map(group => (
                  <div key={group.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`default-group-${group.id}`}
                      checked={onboarding.defaultGroupIds.includes(group.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          updateOnboarding({ defaultGroupIds: [...onboarding.defaultGroupIds, group.id] });
                        } else {
                          updateOnboarding({ defaultGroupIds: onboarding.defaultGroupIds.filter(g => g !== group.id) });
                        }
                      }}
                    />
                    <label htmlFor={`default-group-${group.id}`} className="text-sm cursor-pointer">{group.name}</label>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No groups defined yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Welcome Message */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Welcome Message
          </CardTitle>
          <CardDescription>
            Custom message shown to new users after registration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="Welcome to the Label Maker! Here's how to get started..."
            value={onboarding.welcomeMessage}
            onChange={(e) => updateOnboarding({ welcomeMessage: e.target.value })}
            rows={4}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="w-3 h-3" />
            Leave empty to skip the welcome message
          </p>
        </CardContent>
      </Card>

      {/* Link Defaults */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Invite Link Defaults
          </CardTitle>
          <CardDescription>
            Default values when creating new invite links
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Max Uses</Label>
              <Input
                type="number"
                min={0}
                value={onboarding.defaultMaxUses}
                onChange={(e) => updateOnboarding({ defaultMaxUses: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">0 = unlimited</p>
            </div>
            <div className="space-y-2">
              <Label>Default Expiry (days)</Label>
              <Input
                type="number"
                min={0}
                value={onboarding.defaultExpiryDays}
                onChange={(e) => updateOnboarding({ defaultExpiryDays: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">0 = never expires</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save button for onboarding settings */}
      {onboardingDirty && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button onClick={saveOnboardingSettings} disabled={savingOnboarding} className="shadow-lg gap-2">
            {savingOnboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Onboarding Settings
          </Button>
        </div>
      )}

      {/* Invite Links Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5 text-primary" />
                Invite Links
                {activeLinks.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{activeLinks.length} active</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Generate and manage invite links for user onboarding
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => { resetForm(); setCreateOpen(true); }} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Create Link
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No invite links yet</p>
              <p className="text-xs mt-1">Create your first link to start onboarding users</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active Links */}
              {activeLinks.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Active</h4>
                  {activeLinks.map(link => (
                    <LinkRow key={link.id} link={link} groups={groups} onCopy={copyLink} onToggle={toggleActive} onDelete={deleteLink} getGroupNames={getGroupNames} isExpired={isExpired} isUsedUp={isUsedUp} />
                  ))}
                </div>
              )}

              {/* Inactive / Expired Links */}
              {inactiveLinks.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Inactive / Expired</h4>
                  {inactiveLinks.map(link => (
                    <LinkRow key={link.id} link={link} groups={groups} onCopy={copyLink} onToggle={toggleActive} onDelete={deleteLink} getGroupNames={getGroupNames} isExpired={isExpired} isUsedUp={isUsedUp} />
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invite Link</DialogTitle>
            <DialogDescription>
              Generate a link that lets new users sign up with pre-configured settings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label (optional)</Label>
              <Input
                placeholder="e.g., Warehouse team onboarding"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Uses</Label>
                <Input
                  type="number"
                  min={0}
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">0 = unlimited</p>
              </div>
              <div className="space-y-2">
                <Label>Expires After (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">0 = never</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={autoApprove} onCheckedChange={setAutoApprove} id="autoApproveSwitch" />
              <Label htmlFor="autoApproveSwitch">Auto-approve users</Label>
            </div>
            {groups.length > 0 && (
              <div className="space-y-2">
                <Label>Assign to Groups</Label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                  {groups.map(group => (
                    <div key={group.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`invite-group-${group.id}`}
                        checked={selectedGroups.includes(group.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedGroups(prev => [...prev, group.id]);
                          } else {
                            setSelectedGroups(prev => prev.filter(g => g !== group.id));
                          }
                        }}
                      />
                      <label htmlFor={`invite-group-${group.id}`} className="text-sm">{group.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create & Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Extracted link row component
const LinkRow: React.FC<{
  link: InviteLink;
  groups: GroupItem[];
  onCopy: (code: string) => void;
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  getGroupNames: (ids: string[]) => string;
  isExpired: (link: InviteLink) => boolean | "" | null;
  isUsedUp: (link: InviteLink) => boolean;
}> = ({ link, onCopy, onToggle, onDelete, getGroupNames, isExpired, isUsedUp }) => (
  <div
    className={`flex items-center justify-between p-3 rounded-lg border bg-card ${
      (!link.is_active || isExpired(link) || isUsedUp(link)) ? 'opacity-60' : ''
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm font-medium">{link.code}</span>
        {link.label && (
          <span className="text-xs text-muted-foreground">— {link.label}</span>
        )}
        {!link.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
        {isExpired(link) && <Badge variant="destructive" className="text-xs">Expired</Badge>}
        {isUsedUp(link) && <Badge variant="secondary" className="text-xs">Used up</Badge>}
        {link.auto_approve && <Badge variant="outline" className="text-[10px]">Auto-approve</Badge>}
      </div>
      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          {link.uses}/{link.max_uses > 0 ? link.max_uses : '∞'} uses
        </span>
        {link.group_ids.length > 0 && (
          <span>Groups: {getGroupNames(link.group_ids)}</span>
        )}
        {link.expires_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {isExpired(link) ? 'Expired' : `Expires ${formatDistanceToNow(new Date(link.expires_at), { addSuffix: true })}`}
          </span>
        )}
      </div>
    </div>
    <div className="flex items-center gap-1 ml-2">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onCopy(link.code)}>
        <Copy className="w-3.5 h-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onToggle(link.id, !link.is_active)}>
        {link.is_active ? <CheckCircle className="w-3.5 h-3.5 text-primary" /> : <CheckCircle className="w-3.5 h-3.5" />}
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(link.id)}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);
